import React, { useEffect, useMemo, useState } from "react";
import { formatISTDate } from "../utils/dateTime";

function MiscellaneousBillModal({ show, onClose, clinicName, clinic }) {
  const [form, setForm] = useState({
    petName: "",
    age: "",
    color: "",
    weight: "",
    mobileNumber: ""
  });

  const [item, setItem] = useState({
    description: "",
    quantity: "1",
    rate: ""
  });

  const [items, setItems] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("danger");

  useEffect(() => {
    if (!show) return;

    setForm({
      petName: "",
      age: "",
      color: "",
      weight: "",
      mobileNumber: ""
    });

    setItem({
      description: "",
      quantity: "1",
      rate: ""
    });

    setItems([]);
    setMessage("");
    setMessageType("danger");
  }, [show]);

  const total = useMemo(() => {
    return items.reduce((sum, row) => {
      const qty = Number(row.quantity) || 0;
      const rate = Number(row.rate) || 0;
      return sum + qty * rate;
    }, 0);
  }, [items]);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItem((prev) => ({ ...prev, [name]: value }));
  };

  const addItem = () => {
    if (!item.description.trim() || !item.quantity || !item.rate) {
      setMessage("Please fill item description, quantity and rate");
      setMessageType("danger");
      return;
    }

    setItems((prev) => [
      ...prev,
      {
        description: item.description.trim(),
        quantity: Number(item.quantity) || 1,
        rate: Number(item.rate) || 0
      }
    ]);

    setItem({
      description: "",
      quantity: "1",
      rate: ""
    });

    setMessage("Item added to bill");
    setMessageType("success");
  };

  const removeItem = (indexToRemove) => {
    setItems((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handlePrint = () => {
    if (!form.petName.trim() || !form.age.trim() || !form.color.trim() || !form.weight.trim() || !form.mobileNumber.trim()) {
      setMessage("Please fill all pet details before printing");
      setMessageType("danger");
      return;
    }

    if (items.length === 0) {
      setMessage("Please add at least one bill item");
      setMessageType("danger");
      return;
    }

    const receiptNo = `MB-${new Date().getTime()}`;
    const todayFormatted = formatISTDate(new Date());
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }

            body {
              font-family: Arial, sans-serif;
              color: #333;
            }

            .container {
              width: 100%;
            }

            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 16px;
            }

            .logo {
              width: 220px;
              height: auto;
              object-fit: contain;
            }

            .clinic {
              max-width: 60%;
            }

            .right {
              text-align: right;
              min-width: 180px;
            }

            .line {
              border-top: 1px solid #ccc;
              margin: 15px 0;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th, td {
              padding: 10px;
            }

            th {
              border-bottom: 2px solid #000;
              text-align: left;
            }

            td {
              border-bottom: 1px solid #ddd;
            }

            .text-right {
              text-align: right;
            }

            .total {
              text-align: right;
              margin-top: 20px;
              font-size: 18px;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div>
                ${
                  clinic?.logoUrl
                    ? `<img src="${clinic.logoUrl}" class="logo" alt="Clinic Logo" />`
                    : ""
                }
              </div>

              <div class="clinic">
                <h2>${clinic?.name || clinicName || "Himalayan Pet Clinic"}</h2>
                <p>
                  ${clinic?.address1 || ""}<br/>
                  ${clinic?.address2 || ""}<br/>
                  ${clinic?.city || ""}- ${clinic?.state || ""} - ${clinic?.pin || ""}<br/>
                  ☎ ${clinic?.phone || ""}
                </p>
              </div>

              <div class="right">
                <p><b>${receiptNo}</b></p>
                <p><b>DATE</b><br/>${todayFormatted}</p>
                <p><b>DUE</b><br/>On Receipt</p>
              </div>
            </div>

            <div class="line"></div>

            <p><b>BILL TO</b></p>
            <p style="font-size:16px; margin-bottom:6px;">
              <b>${form.petName}</b>
            </p>
            <div style="font-size:14px; line-height:1.6;">
              <div><b>Age:</b> ${form.age}</div>
              <div><b>Color:</b> ${form.color}</div>
              <div><b>Weight:</b> ${form.weight}</div>
              <div><b>Mobile:</b> ${form.mobileNumber}</div>
            </div>

            <div class="line"></div>

            <table>
              <thead>
                <tr>
                  <th>DESCRIPTION</th>
                  <th class="text-right">RATE</th>
                  <th class="text-right">QTY</th>
                  <th class="text-right">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                ${items
                  .map(
                    (row) => `
                      <tr>
                        <td>${row.description}</td>
                        <td class="text-right">${Number(row.rate).toFixed(2)}</td>
                        <td class="text-right">${row.quantity}</td>
                        <td class="text-right">${(Number(row.quantity) * Number(row.rate)).toFixed(2)}</td>
                      </tr>
                    `
                  )
                  .join("")}
              </tbody>
            </table>

            <div class="total">
              TOTAL: ₹${total.toFixed(2)}
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal show fade d-block">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Bills</h5>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body">
              {message && (
                <div className={`alert alert-${messageType} py-2`}>{message}</div>
              )}

              <div className="row g-2">
                <div className="col-md-6">
                  <input
                    className="form-control"
                    name="petName"
                    placeholder="Pet Name"
                    value={form.petName}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    className="form-control"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={form.mobileNumber}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    className="form-control"
                    name="age"
                    placeholder="Age"
                    value={form.age}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    className="form-control"
                    name="color"
                    placeholder="Color"
                    value={form.color}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    className="form-control"
                    name="weight"
                    placeholder="Weight"
                    value={form.weight}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <hr />

              <h6 className="mb-2">Add Items</h6>
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <input
                    className="form-control"
                    name="description"
                    placeholder="Item Description"
                    value={item.description}
                    onChange={handleItemChange}
                  />
                </div>
                <div className="col-md-2">
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    name="quantity"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={handleItemChange}
                  />
                </div>
                <div className="col-md-2">
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    name="rate"
                    placeholder="Rate"
                    value={item.rate}
                    onChange={handleItemChange}
                  />
                </div>
                <div className="col-md-2 d-grid">
                  <button className="btn btn-primary" type="button" onClick={addItem}>
                    Add
                  </button>
                </div>
              </div>

              {items.length > 0 && (
                <div className="table-responsive mt-3">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Rate</th>
                        <th>Amount</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row, index) => (
                        <tr key={`${row.description}-${index}`}>
                          <td>{row.description}</td>
                          <td>{row.quantity}</td>
                          <td>{row.rate.toFixed(2)}</td>
                          <td>{(row.quantity * row.rate).toFixed(2)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              type="button"
                              onClick={() => removeItem(index)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mt-3">
                <strong>Total: ₹{total.toFixed(2)}</strong>
                <small className="text-muted">Items: {items.length}</small>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn btn-success" type="button" onClick={handlePrint}>
                Print Bill
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MiscellaneousBillModal;
